import React, { useState } from 'react';
import { Mail, Phone, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    type: ''
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    // Ici on ajoutera la logique d'envoi
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      details: 'padaid.asso@gmail.com',
      subtitle: 'Réponse sous 24h'
    },

    {
      icon: Clock,
      title: 'Horaires',
      details: 'Lun-Ven: 9h00-18h00\nSam: 10h00-14h00',
      subtitle: 'Accueil téléphonique'
    }
  ];

  const faqs = [
    {
      question: 'Comment devenir bénévole ?',
      answer: 'Remplissez le formulaire de contact en précisant votre souhait de bénévolat. Nous vous recontacterons pour discuter des opportunités disponibles.'
    },
    {
      question: 'Où vont mes dons ?',
      answer: 'Nous publions chaque année un rapport financier détaillant l\'utilisation de tous les dons. 85% sont directement affectés aux programmes terrain.'
    },
    {
      question: 'Puis-je visiter vos projets ?',
      answer: 'Des voyages solidaires sont organisés plusieurs fois par an. Contactez-nous pour connaître les prochaines dates et modalités.'
    }
  ];

  return (
    <>
      <div className="relative z-10 min-h-screen py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Contactez-nous</h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Une question, une suggestion, ou envie de vous engager ? 
            Notre équipe est là pour vous accompagner dans votre démarche solidaire.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card className="border-border/50">
              <CardHeader>
                <h3 className="text-2xl font-semibold flex items-center">
                  <MessageCircle className="h-6 w-6 mr-2 text-primary" />
                  Envoyez-nous un message
                </h3>
                <p className="text-muted-foreground">
                  Remplissez le formulaire ci-dessous et nous vous répondrons rapidement.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Nom complet *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Votre nom"
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange('email', e.target.value)}
                        placeholder="votre.email@exemple.com"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="type">Type de demande</Label>
                    <Select onValueChange={(value) => handleInputChange('type', value)}>
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez le type de votre demande" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="volunteer">Bénévolat</SelectItem>
                        <SelectItem value="donation">Don / Parrainage</SelectItem>
                        <SelectItem value="partnership">Partenariat</SelectItem>
                        <SelectItem value="media">Demande média</SelectItem>
                        <SelectItem value="general">Question générale</SelectItem>
                        <SelectItem value="other">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="subject">Sujet *</Label>
                    <Input
                      id="subject"
                      value={formData.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      placeholder="Résumez votre demande en quelques mots"
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      placeholder="Décrivez votre demande en détail..."
                      className="min-h-[120px]"
                      required
                    />
                  </div>

                  <Button type="submit" size="lg" className="w-full bg-primary hover:bg-primary/90">
                    <Send className="h-4 w-4 mr-2" />
                    Envoyer le message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Contact Info */}
          <div className="space-y-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="border-border/50 hover:border-primary/20 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-start space-x-3">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                          {info.title}
                        </h4>
                        <p className="font-semibold mt-1 whitespace-pre-line">
                          {info.details}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {info.subtitle}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-center mb-8">Questions fréquentes</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {faqs.map((faq, index) => (
              <Card key={index} className="border-border/50 hover:border-primary/20 transition-colors">
                <CardContent className="p-6">
                  <h4 className="font-semibold mb-3 text-primary">
                    {faq.question}
                  </h4>
                  <p className="text-muted-foreground text-sm">
                    {faq.answer}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default Contact;